/*
 * Copyright (c) 2019 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, August 2019
 */

import 'core-js';
import 'whatwg-fetch';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/browser';
import { ErrorBoundary } from 'react-error-boundary';
import FlowPage from './concepts/flow/flow-page';
import ErrorScreen from './concepts/flow/error-screen';

export default class FlowApplication {
  static run(initialData: Record<string, any>): void {
    const {
      targetId,
      sessionId,
      flowId,
      bridgeId,
      customerId,
      interactionId,
      source,
      medium,
      reason,
      flowObjects,
      localeId,
      platform,
      parameters,
      themeColors,
      status,
      versionId,
      flowState,
    } = initialData;

    Sentry?.init({
      dsn: 'https://1a2a901cc67348339c2b80e0c37a0db4@o195349.ingest.sentry.io/5559710',
      environment: process.env.NODE_ENV || 'development',
      release: process.env.RELEASE,
    });
    Sentry.setContext('flow', {
      app: 'customer-app',
      targetId,
      sessionId,
      flowId,
      customerId,
      interactionId,
      source,
      medium,
      reason,
      localeId,
      platform,
      parameters,
      status,
      versionId,
    });

    const trackException = (error, info) => {
      Sentry.captureException(error, {
        extra: info,
      });
    };

    const restartFlow = () => {
      location.reload();
    };

    async function clearStorages() {
      if (caches) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }
      if (localStorage) {
        localStorage.clear();
      }
    }
    async function restartWorker() {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.unregister();
        }
      }
    }
    const resetCacheAndRestartFlow = () => {
      if (platform === 'kiosk' || platform === 'pos') {
        clearStorages().then(restartWorker).then(restartFlow);
      } else {
        restartFlow();
      }
    };

    const container = document.getElementById('flow-root');
    const root = createRoot(container);
    root.render(
      <HelmetProvider>
        <ErrorBoundary
          FallbackComponent={ErrorScreen}
          onError={trackException}
          onReset={resetCacheAndRestartFlow}
        >
          <FlowPage
            targetId={targetId}
            sessionId={sessionId}
            flowId={flowId}
            customerId={customerId}
            interactionId={interactionId}
            source={source}
            medium={medium}
            reason={reason}
            platform={platform}
            themeColors={themeColors}
            flowObjects={flowObjects}
            bridgeId={bridgeId}
            localeId={localeId}
            parameters={parameters}
            status={status}
            versionId={versionId}
            flowState={flowState}
          />
        </ErrorBoundary>
      </HelmetProvider>,
    );
  }
}
